import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as api from './monitoringApi';
import { MonitoringUnavailableError, monitoringErrorMessage } from './monitoringApi';
import { SEVERITY_RANK } from './format';
import type {
  AlertQuery,
  AlertStatus,
  AlertView,
  CreateWatchInput,
  MonitoringOverview,
  SignalQuery,
  SignalStatus,
  SignalView,
  UpdateWatchInput,
  WatchFrequency,
  WatchSubjectType,
  WatchView,
} from '../../types/monitoring';

export interface LoadState {
  loading: boolean;
  /** One short line, already human-readable */
  error: string | null;
  /** The endpoints do not exist on this server yet */
  unavailable: boolean;
}

const failure = (error: any): Pick<LoadState, 'error' | 'unavailable'> => ({
  error: monitoringErrorMessage(error),
  unavailable: error instanceof MonitoringUnavailableError,
});

/* ------------------------------------------------------------------ */
/* Overview                                                            */
/* ------------------------------------------------------------------ */

export function useOverview() {
  const [overview, setOverview] = useState<MonitoringOverview | null>(null);
  const [state, setState] = useState<LoadState>({ loading: true, error: null, unavailable: false });

  const reload = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      setOverview(await api.fetchOverview());
      setState({ loading: false, error: null, unavailable: false });
    } catch (error) {
      setState({ loading: false, ...failure(error) });
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  // A scan runs for minutes; while one is in flight the page follows it
  const running = !!overview?.scan?.running;
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(reload, 5000);
    return () => clearInterval(timer);
  }, [running, reload]);

  return { overview, ...state, reload };
}

/* ------------------------------------------------------------------ */
/* Alerts                                                              */
/* ------------------------------------------------------------------ */

/** Open first, then most severe, then newest — the order an analyst wants to work in */
export function sortAlerts(alerts: AlertView[]): AlertView[] {
  const statusRank: Record<AlertStatus, number> = { open: 0, acknowledged: 1, resolved: 2 };
  return [...alerts].sort(
    (a, b) =>
      statusRank[a.status] - statusRank[b.status] ||
      SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] ||
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

export function useAlerts(query: AlertQuery = {}) {
  const key = JSON.stringify(query);
  const [alerts, setAlerts] = useState<AlertView[]>([]);
  const [state, setState] = useState<LoadState>({ loading: true, error: null, unavailable: false });
  const [busyId, setBusyId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const data = await api.listAlerts(JSON.parse(key));
      setAlerts(sortAlerts(data));
      setState({ loading: false, error: null, unavailable: false });
    } catch (error) {
      setState({ loading: false, ...failure(error) });
    }
  }, [key]);

  useEffect(() => {
    reload();
  }, [reload]);

  const setStatus = useCallback(async (id: string, status: AlertStatus) => {
    setBusyId(id);
    try {
      const updated = await api.setAlertStatus(id, status);
      setAlerts((current) => sortAlerts(current.map((a) => (a.id === id ? { ...a, ...updated } : a))));
      setState((s) => ({ ...s, error: null }));
    } catch (error) {
      setState((s) => ({ ...s, ...failure(error) }));
    } finally {
      setBusyId(null);
    }
  }, []);

  return { alerts, ...state, busyId, reload, setStatus };
}

/* ------------------------------------------------------------------ */
/* Signals                                                             */
/* ------------------------------------------------------------------ */

export function useSignals(query: SignalQuery) {
  const key = JSON.stringify(query);
  const [items, setItems] = useState<SignalView[]>([]);
  const [total, setTotal] = useState(0);
  const [cursor, setCursor] = useState<string | null>(null);
  const [state, setState] = useState<LoadState>({ loading: true, error: null, unavailable: false });
  const [loadingMore, setLoadingMore] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Only the newest request may write results: filters change faster than the network answers
  const requestRef = useRef(0);

  const reload = useCallback(async () => {
    const request = ++requestRef.current;
    setState((s) => ({ ...s, loading: true }));
    try {
      const page = await api.listSignals(JSON.parse(key));
      if (requestRef.current !== request) return;
      setItems(page.items);
      setTotal(page.total);
      setCursor(page.nextCursor);
      setState({ loading: false, error: null, unavailable: false });
    } catch (error) {
      if (requestRef.current !== request) return;
      setItems([]);
      setState({ loading: false, ...failure(error) });
    }
  }, [key]);

  useEffect(() => {
    reload();
  }, [reload]);

  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;
    const request = requestRef.current;
    setLoadingMore(true);
    try {
      const page = await api.listSignals({ ...JSON.parse(key), cursor });
      if (requestRef.current !== request) return;
      setItems((current) => {
        const seen = new Set(current.map((s) => s.id));
        return [...current, ...page.items.filter((s) => !seen.has(s.id))];
      });
      setTotal(page.total);
      setCursor(page.nextCursor);
    } catch (error) {
      setState((s) => ({ ...s, ...failure(error) }));
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, key, loadingMore]);

  const setStatus = useCallback(async (id: string, status: SignalStatus) => {
    setBusyId(id);
    try {
      const updated = await api.setSignalStatus(id, status);
      setItems((current) => current.map((s) => (s.id === id ? { ...s, ...updated } : s)));
      setState((s) => ({ ...s, error: null }));
    } catch (error) {
      setState((s) => ({ ...s, ...failure(error) }));
    } finally {
      setBusyId(null);
    }
  }, []);

  return { items, total, ...state, loadingMore, hasMore: !!cursor, busyId, reload, loadMore, setStatus };
}

/* ------------------------------------------------------------------ */
/* Watches                                                             */
/* ------------------------------------------------------------------ */

export function useWatches() {
  const [watches, setWatches] = useState<WatchView[]>([]);
  const [state, setState] = useState<LoadState>({ loading: true, error: null, unavailable: false });
  const [busyId, setBusyId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      setWatches(await api.listWatches());
      setState({ loading: false, error: null, unavailable: false });
    } catch (error) {
      setState({ loading: false, ...failure(error) });
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const run = useCallback(async <T,>(id: string, task: () => Promise<T>): Promise<T | null> => {
    setBusyId(id);
    try {
      const result = await task();
      setState((s) => ({ ...s, error: null }));
      return result;
    } catch (error) {
      setState((s) => ({ ...s, ...failure(error) }));
      return null;
    } finally {
      setBusyId(null);
    }
  }, []);

  const create = useCallback(
    (input: CreateWatchInput | { items: CreateWatchInput[] }) =>
      run('new', async () => {
        const created = await api.createWatch(input);
        setWatches((current) => {
          const ids = new Set(created.map((w) => w.id));
          return [...created, ...current.filter((w) => !ids.has(w.id))];
        });
        return created;
      }),
    [run],
  );

  const update = useCallback(
    (id: string, input: UpdateWatchInput) =>
      run(id, async () => {
        const updated = await api.updateWatch(id, input);
        setWatches((current) => current.map((w) => (w.id === id ? { ...w, ...updated } : w)));
        return updated;
      }),
    [run],
  );

  const remove = useCallback(
    (id: string) =>
      run(id, async () => {
        await api.deleteWatch(id);
        setWatches((current) => current.filter((w) => w.id !== id));
      }),
    [run],
  );

  const scan = useCallback(
    (id: string) =>
      run(id, async () => {
        const result = await api.scanWatch(id);
        setWatches((current) =>
          current.map((w) =>
            w.id === id
              ? {
                  ...w,
                  lastScanAt: new Date().toISOString(),
                  lastError: result.error || null,
                  signalCount: w.signalCount + result.stored,
                  recentSignals: w.recentSignals + result.stored,
                }
              : w,
          ),
        );
        return result;
      }),
    [run],
  );

  const scanAll = useCallback(async () => {
    const summary = await run('all', () => api.scanAll());
    if (summary) await reload();
    return summary;
  }, [run, reload]);

  return { watches, ...state, busyId, reload, create, update, remove, scan, scanAll };
}

/** The watch on one entity, for the per-entity panel on a detail page */
export function useEntityWatch(subjectType: WatchSubjectType, subjectId: string) {
  const [watch, setWatch] = useState<WatchView | null>(null);
  const [state, setState] = useState<LoadState>({ loading: true, error: null, unavailable: false });
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const all = await api.listWatches();
      setWatch(all.find((w) => w.subject.id === subjectId) || null);
      setState({ loading: false, error: null, unavailable: false });
    } catch (error) {
      setState({ loading: false, ...failure(error) });
    }
  }, [subjectId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const start = useCallback(
    async (frequency: WatchFrequency = 'weekly') => {
      setSaving(true);
      try {
        const [created] = await api.createWatch({ subjectType, subjectId, frequency });
        if (created) setWatch(created);
        setState((s) => ({ ...s, error: null }));
      } catch (error) {
        setState((s) => ({ ...s, ...failure(error) }));
      } finally {
        setSaving(false);
      }
    },
    [subjectId, subjectType],
  );

  const setFrequency = useCallback(
    async (frequency: WatchFrequency) => {
      if (!watch) return;
      setSaving(true);
      try {
        const updated = await api.updateWatch(watch.id, { frequency });
        setWatch((current) => (current ? { ...current, ...updated } : updated));
        setState((s) => ({ ...s, error: null }));
      } catch (error) {
        setState((s) => ({ ...s, ...failure(error) }));
      } finally {
        setSaving(false);
      }
    },
    [watch],
  );

  const stop = useCallback(async () => {
    if (!watch) return;
    setSaving(true);
    try {
      await api.deleteWatch(watch.id);
      setWatch(null);
      setState((s) => ({ ...s, error: null }));
    } catch (error) {
      setState((s) => ({ ...s, ...failure(error) }));
    } finally {
      setSaving(false);
    }
  }, [watch]);

  return { watch, ...state, saving, reload, start, setFrequency, stop };
}

/* ------------------------------------------------------------------ */
/* Open alert count (header badge)                                     */
/* ------------------------------------------------------------------ */

const POLL_MS = 60_000;

/**
 * One shared count for the whole app: every subscriber reads the same value and
 * a single timer refreshes it, so mounting the badge twice does not double the
 * requests. Refreshes on window focus, and stops once the endpoint 404s.
 */
const alertCountStore = {
  count: 0,
  listeners: new Set<(count: number) => void>(),
  fetchedAt: 0,
  inFlight: null as Promise<void> | null,
  stopped: false,
  set(count: number) {
    this.count = count;
    this.listeners.forEach((listener) => listener(count));
  },
  refresh(force = false): Promise<void> {
    if (this.stopped) return Promise.resolve();
    if (this.inFlight) return this.inFlight;
    if (!force && Date.now() - this.fetchedAt < POLL_MS / 2) return Promise.resolve();
    this.inFlight = api
      .listAlerts({ status: 'open', limit: 100 })
      .then((alerts) => {
        this.fetchedAt = Date.now();
        this.set(alerts.length);
      })
      .catch((error) => {
        // A missing endpoint will not appear by polling harder
        if (error instanceof MonitoringUnavailableError) this.stopped = true;
        this.set(0);
      })
      .finally(() => {
        this.inFlight = null;
      });
    return this.inFlight;
  },
};

/** Test seam: forget the cached count and any 404 latch */
export function __resetAlertCount() {
  alertCountStore.count = 0;
  alertCountStore.fetchedAt = 0;
  alertCountStore.stopped = false;
  alertCountStore.inFlight = null;
}

export function useOpenAlertCount(enabled = true): number {
  const [count, setCount] = useState(alertCountStore.count);

  useEffect(() => {
    if (!enabled) return;
    const listener = (next: number) => setCount(next);
    alertCountStore.listeners.add(listener);
    alertCountStore.refresh();
    const timer = window.setInterval(() => alertCountStore.refresh(), POLL_MS);
    const onFocus = () => alertCountStore.refresh();
    window.addEventListener('focus', onFocus);
    return () => {
      alertCountStore.listeners.delete(listener);
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled]);

  return count;
}

/* ------------------------------------------------------------------ */
/* Filter state                                                        */
/* ------------------------------------------------------------------ */

export interface SignalFilters {
  subject: { type: 'stakeholder' | 'organization'; id: string; label: string } | null;
  status: SignalQuery['status'] | '';
  category: SignalQuery['category'] | '';
  severity: SignalQuery['severity'] | '';
  /** Days back, '' for any time */
  days: '' | '1' | '7' | '30' | '90';
  search: string;
}

export const EMPTY_FILTERS: SignalFilters = {
  subject: null,
  status: '',
  category: '',
  severity: '',
  days: '',
  search: '',
};

export const activeFilterCount = (filters: SignalFilters): number =>
  (filters.subject ? 1 : 0) +
  (filters.status ? 1 : 0) +
  (filters.category ? 1 : 0) +
  (filters.severity ? 1 : 0) +
  (filters.days ? 1 : 0) +
  (filters.search.trim() ? 1 : 0);

/** Turns the filter form into the query the API takes, dropping anything unset */
export function toSignalQuery(filters: SignalFilters, limit = 20): SignalQuery {
  const query: SignalQuery = { limit };
  if (filters.subject) {
    query.subjectType = filters.subject.type;
    query.subjectId = filters.subject.id;
  }
  if (filters.status) query.status = filters.status;
  if (filters.category) query.category = filters.category;
  if (filters.severity) query.severity = filters.severity;
  if (filters.search.trim()) query.search = filters.search.trim();
  if (filters.days) {
    const since = new Date();
    since.setDate(since.getDate() - Number(filters.days));
    query.since = since.toISOString();
  }
  return query;
}

/** Stable query object: only rebuilt when a filter actually changes */
export function useSignalQuery(filters: SignalFilters, limit = 20): SignalQuery {
  // `days` becomes a moving timestamp, so memoise on the filters, not the result
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => toSignalQuery(filters, limit), [JSON.stringify(filters), limit]);
}
